import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { Card, Dialog } from 'Components/ui';
import styled from 'styled-components';

import {
  acceptPolicyVersion,
  getPolicyVersionUserById,
  markPolicyVersionAsRead,
  regenerateAttachmentDownloadUrl,
} from 'services/policies-service';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';

import { PageLoaderAcceptPolicy } from 'Components/PageLoader/AcceptPolicy';
import { ButtonForm } from 'Components/Form/styles';

import { Footer } from 'Components/Footer';

import { TaskAltIcon, AttachFileIcon, PdfIcon, DocIcon } from 'Components/common/Icons';

import 'react-quill/dist/quill.snow.css';

const FormWrapper = styled.div`
  width: 100%;
  padding-inline: 16px;
  padding-bottom: 60px;
  font-family: var(--font-body);
`;

const AttachmentCard = styled(Card)`
  margin-bottom: 16px;
`;

const AttachmentTitleRow = styled.span`
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const DividerLine = styled.hr`
  border: none;
  border-top: 1px solid var(--vo-cloud);
  margin-bottom: 16px;
`;

const AttachmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const AttachmentName = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const AttachmentMeta = styled.span`
  font-size: 14px;
  color: var(--vo-slate);
`;

const SectionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  display: block;
  margin-bottom: 8px;
`;

const ErrorBanner = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background-color: var(--vo-warning-10);
  border-radius: 8px;
  border: 1px solid var(--vo-warning-10);
`;

const ErrorText = styled.span`
  font-size: 14px;
  color: var(--vo-slate);
  display: block;
  margin-bottom: 8px;
`;

const IframeWrapper = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: 8px;
  overflow: hidden;
  height: 600px;
  width: 100%;
`;

const NoPreviewBox = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: var(--vo-capri-25);
  border-radius: 8px;
`;

const StyledTableContainer = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin-bottom: 1rem;
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: 650px;
  border-collapse: collapse;
`;

const StyledTh = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: var(--vo-ink);
  border-bottom: 2px solid var(--vo-cloud);
  background-color: var(--vo-cloud);
`;

const StyledTd = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: var(--vo-ink);
  border-bottom: 1px solid var(--vo-cloud);
`;

const StatusRow = styled.div`
  vertical-align: middle;
  display: flex;
`;

const AcceptedText = styled.div`
  text-align: right;
  color: var(--vo-slate);
  font-size: 1rem;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 1rem;
`;

export const AcceptPolicyVersion = () => {
  const { idPolicyVersionUser } = useParams();
  const [refetch, setRefetch] = useState(false);
  const { t, i18n } = useTranslation(['policy']);

  const getFileIcon = (mimetype) => {
    if (mimetype === 'application/pdf') {
      return <PdfIcon size={24} fill={'var(--vo-danger)'} />;
    } else if (mimetype === 'application/msword' || mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return <DocIcon size={24} fill={'var(--vo-info)'} />;
    }
    return <AttachFileIcon size={24} fill={'var(--vo-slate)'} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const AttachmentViewer = React.memo(({ attachment }) => {
    const [attachmentUrl, setAttachmentUrl] = useState(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const iframeRef = React.useRef(null);

    React.useEffect(() => {
      const init = async () => {
        if (!attachment) return;

        try {
          const response = await regenerateAttachmentDownloadUrl(policyVersionUser.policyId, policyVersionUser.policyVersionId);
          if (response.data?.url) {
            setAttachmentUrl(response.data.url);
          } else {
            console.error("❌ Backend não retornou URL válida");
          }
        } catch (err) {
          setAttachmentUrl(attachment.url);
        }
      };

      init();
    }, [attachment]);

    if (!attachment) return null;

    const regenerateUrlAutomatically = async () => {
      try {
        setIsRegenerating(true);

        const response = await regenerateAttachmentDownloadUrl(policyVersionUser.policyId, policyVersionUser.policyVersionId);

        if (response.data?.url) {
          setAttachmentUrl(response.data.url);
          setIframeError(false);
        } else {
          setIframeError(true);
        }
      } catch (error) {
        setIframeError(true);
      } finally {
        setIsRegenerating(false);
      }
    };

    const regenerateUrl = async () => {
      try {
        setIsRegenerating(true);
        const response = await regenerateAttachmentDownloadUrl(policyVersionUser.policyId, policyVersionUser.policyVersionId);
        if (response.data?.url) {
          setAttachmentUrl(response.data.url);
          setIframeError(false);
          Dialog.alert({
            title: t('policy:versionShow.swal.successTitle'),
            message: response.data.message || t('policy:versionShow.swal.urlRegenSuccess'),
            icon: 'success',
          });
        }
      } catch (error) {
        Dialog.alert({
          title: t('policy:versionShow.swal.errorTitle'),
          message: t('policy:versionShow.swal.urlRegenError'),
          icon: 'error',
        });
      } finally {
        setIsRegenerating(false);
      }
    };

    const handleIframeLoad = () => {
      setIframeError(false);

      setTimeout(() => {
        try {
          const iframe = iframeRef.current;
          if (iframe && iframe.contentDocument) {
            const content = iframe.contentDocument.body?.innerText || '';

            if (content.includes('Access Denied') ||
                content.includes('AccessDenied') ||
                content.includes('Forbidden') ||
                content.includes('403') ||
                content.includes('Token has expired') ||
                content.includes('SignatureDoesNotMatch') ||
                content.includes('ExpiredToken') ||
                content.includes('The provided token has expired')) {
              setIframeError(true);
              regenerateUrlAutomatically();
            }
          }
        } catch (error) {
          console.log('Não foi possível verificar conteúdo do iframe (CORS):', error.message);
        }
      }, 1000);
    };

    const handleIframeError = () => {
      setIframeError(true);
      regenerateUrlAutomatically();
    };

    return (
      <AttachmentCard>
        <div style={{ padding: '24px' }}>
          <AttachmentTitleRow>
            <AttachFileIcon />
            {t('policy:versionShow.attachment.title')}
          </AttachmentTitleRow>
          <DividerLine />

          <AttachmentHeader>
            {getFileIcon(attachment.mimetype)}
            <div>
              <AttachmentName>
                {attachment.originalName}
              </AttachmentName>
              <AttachmentMeta>
                {attachment.mimetype} • {formatFileSize(attachment.size)}
              </AttachmentMeta>
            </div>
          </AttachmentHeader>

          {attachment.mimetype === 'application/pdf' && (
            <div style={{ marginTop: '16px' }}>
              <SectionTitle>
                {t('policy:versionShow.attachment.pdfPreview')}
              </SectionTitle>

              {iframeError && (
                <ErrorBanner>
                  <ErrorText>
                    {t('policy:versionShow.attachment.linkExpired')}
                  </ErrorText>
                  <ButtonForm
                    variant="secondary"
                    onClick={regenerateUrl}
                    disabled={isRegenerating}
                  >
                    {isRegenerating ? t('policy:versionShow.attachment.reloading') : t('policy:versionShow.attachment.reloadDocument')}
                  </ButtonForm>
                </ErrorBanner>
              )}

              <IframeWrapper>
                <iframe
                  ref={iframeRef}
                  key={attachmentUrl}
                  src={attachmentUrl || 'about:blank'}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title={attachment.originalName}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                >
                  <p>
                    {t('policy:versionShow.attachment.iframeFallback')}
                    <a href={attachmentUrl || '#'} target="_blank" rel="noopener noreferrer">
                      {t('policy:versionShow.attachment.iframeFallbackLink')}
                    </a>
                  </p>
                </iframe>
              </IframeWrapper>
            </div>
          )}

          {attachment.mimetype !== 'application/pdf' && (
            <NoPreviewBox>
              <AttachmentMeta>
                {t('policy:versionShow.attachment.noPreview')}
              </AttachmentMeta>
            </NoPreviewBox>
          )}
        </div>
      </AttachmentCard>
    );
  });

  AttachmentViewer.displayName = 'AttachmentViewer';

  const {
    isLoading,
    error,
    isSuccess,
    data: policyVersionUser,
  } = useQuery(['getPolicyVersionUserById', refetch], async () => {
    const resPolicyVersionUser = await getPolicyVersionUserById(idPolicyVersionUser);

    if (resPolicyVersionUser.status === 200) return resPolicyVersionUser.data;

    return null;
  });

  const policyVersion = policyVersionUser?.policy?.policyVersions?.[policyVersionUser?.policyVersionId];

  const markAsReadPolicyVersion = useCallback(async () => {
    try {
      if (!idPolicyVersionUser) return;
      if (!policyVersionUser) return;
      await markPolicyVersionAsRead(idPolicyVersionUser);
    } catch (err) {
      Dialog.alert({
        title: t('policy:versionShow.swal.errorTitle'),
        message: err.response.data.message ?? err.message,
        icon: 'error',
      });
    }
  }, [idPolicyVersionUser, policyVersionUser, t]);

  const acceptPolicyVersionAction = useCallback(async () => {
    try {
      await acceptPolicyVersion(idPolicyVersionUser);
      setRefetch(true);
      Dialog.alert({
        title: t('policy:versionShow.swal.successTitle'),
        message: t('policy:acceptPolicy.swal.acceptSuccess'),
        icon: 'success',
      });
    } catch (err) {
      Dialog.alert({
        title: t('policy:versionShow.swal.errorTitle'),
        message: err.response.data.message ?? err.message,
        icon: 'error',
      });
    }
  }, [idPolicyVersionUser, t]);

  useEffect(() => {
    if (!idPolicyVersionUser) return;
    if (!policyVersionUser) return;
    if (error) return;
    if (!isSuccess) return;
    if (policyVersionUser.readed === true) return;

    markAsReadPolicyVersion();
  }, [idPolicyVersionUser, policyVersionUser, error, isSuccess, markAsReadPolicyVersion]);

  useEffect(() => {
    const lang = policyVersionUser?.user?.preferredLanguage;
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [policyVersionUser, i18n]);

  const getStatusReview = () => {
    return (
      <StatusRow>
        <TaskAltIcon fill={'var(--vo-success)'} />
        &nbsp;{t('policy:versionShow.statusReviewed')}
      </StatusRow>
    );
  };

  const getStatusApprove = () => {
    return (
      <StatusRow>
        <TaskAltIcon fill={'var(--vo-success)'} />
        &nbsp;{t('policy:versionShow.statusApproved')}
      </StatusRow>
    );
  };

  const getStatusPublish = () => {
    return (
      <StatusRow>
        <TaskAltIcon fill={'var(--vo-success)'} />
        &nbsp;{t('policy:versionShow.statusPublished')}
      </StatusRow>
    );
  };

  const getFormAcceptPolicyVersion = () => {
    return (
      <>
        <FormWrapper className="container">
          <div>
            <h3
              style={{
                textAlign: 'center',
                fontSize: '1.75rem',
                fontWeight: '500',
                marginTop: '1.5rem',
                marginBottom: '0.5rem',
              }}
            >
              {policyVersionUser?.policyName} ({policyVersionUser?.policyVersion})
            </h3>
          </div>
          <AcceptedText>
            {policyVersionUser?.policyPublishedDate
              ? formatDateTimeToDateTimeBR(policyVersionUser.policyPublishedDate)
              : t('policy:versionShow.notPublished')}
          </AcceptedText>
          <div
            style={{ display: 'flex', justifyContent: 'start', flexDirection: 'column' }}
            className="ql-snow ql-editor"
            dangerouslySetInnerHTML={{ __html: policyVersionUser?.policyVersionText }}
          ></div>
           {policyVersion?.attachment && (
            <AttachmentViewer attachment={policyVersion.attachment} />
          )}
          {policyVersion && (
            <StyledTableContainer>
              <StyledTable aria-label="simple table">
                <thead>
                  <tr key={'table-row-header'}>
                    <StyledTh key={`thrc-stage`}>{t('policy:versionShow.table.stage')}</StyledTh>
                    <StyledTh key={`thrc-status`}>{t('policy:versionShow.table.status')}</StyledTh>
                    <StyledTh key={`thrc-date`}>{t('policy:versionShow.table.date')}</StyledTh>
                    <StyledTh key={`thrc-sponsor`}>{t('policy:versionShow.table.responsible')}</StyledTh>
                  </tr>
                </thead>
                <tbody>
                  <tr key={'table-row-header-review'}>
                    <StyledTd key={`thrc-stage-1`}>{t('policy:versionShow.stage.review')}</StyledTd>
                    <StyledTd key={`thrc-status-1`}>{getStatusReview()}</StyledTd>
                    <StyledTd key={`thrc-date-1`}>{formatDateTimeToDateTimeBR(policyVersion?.reviewedDate)}</StyledTd>
                    <StyledTd key={`thrc-sponsor-1`}>{policyVersion?.reviewer?.name}</StyledTd>
                  </tr>
                  <tr key={'table-row-header-approve'}>
                    <StyledTd key={`thrc-stage-2`}>{t('policy:versionShow.stage.approval')}</StyledTd>
                    <StyledTd key={`thrc-status-2`}>{getStatusApprove()}</StyledTd>
                    <StyledTd key={`thrc-date-2`}>{formatDateTimeToDateTimeBR(policyVersion?.approvedDate)}</StyledTd>
                    <StyledTd key={`thrc-sponsor-2`}>{policyVersion?.approver?.name}</StyledTd>
                  </tr>
                  <tr key={'table-row-header-publish'}>
                    <StyledTd key={`thrc-stage-3`}>{t('policy:versionShow.stage.publication')}</StyledTd>
                    <StyledTd key={`thrc-status-3`}>{getStatusPublish()}</StyledTd>
                    <StyledTd key={`thrc-date-3`}>
                      {policyVersion?.publishedDate ? formatDateTimeToDateTimeBR(policyVersion.publishedDate) : t('policy:versionShow.notPublished')}
                    </StyledTd>
                    <StyledTd key={`thrc-sponsor-3`}>{policyVersion?.author?.name}</StyledTd>
                  </tr>
                </tbody>
              </StyledTable>
            </StyledTableContainer>
          )}
          {!policyVersionUser?.accepted && <ButtonForm onClick={() => acceptPolicyVersionAction()}>{t('policy:acceptPolicy.button.accept')}</ButtonForm>}
          {policyVersionUser?.accepted && (
            <div>
              {t('policy:acceptPolicy.alreadyAccepted', { date: formatDateTimeToDateTimeBR(policyVersionUser?.acceptedDate) })}
            </div>
          )}
        </FormWrapper>
      </>
    );
  };

  return (
    <>
      {!isLoading && !error && getFormAcceptPolicyVersion()}
      {isLoading && <PageLoaderAcceptPolicy />}
      {error && <div>{t('policy:page.error')}</div>}
      <Footer />
    </>
  );
};
